<script setup lang="ts">
import { computed, onMounted, onUnmounted, useTemplateRef } from "vue";
import type { EditorImage } from "../editor/port";
import {
  useAnchoredFloating,
  type AnchorSource,
} from "../composables/useAnchoredFloating";
import { isJust, toMaybe, type Maybe } from "../lib/maybe";

const props = defineProps<{
  open: boolean;
  /** The image element this toolbar acts on. */
  anchor: AnchorSource;
  image: Maybe<EditorImage>;
  busy?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  describe: [];
  rotate: [];
  crop: [];
  delete: [];
}>();

const root = useTemplateRef<HTMLElement>("root");

// Teleported past the overflow:hidden editor shell, and flipped above the image
// when it sits too close to the bottom of the window.
const panel = computed(() => toMaybe(root.value));
const { floatingStyle } = useAnchoredFloating(
  () => props.anchor,
  panel,
  () => props.open === true && isJust(props.image),
  { placement: "bottom", gap: 8 },
);

function onDocPointer(event: PointerEvent): void {
  if (props.open === false) return;
  const target = event.target;
  const rootEl = root.value;
  if (target instanceof Node && rootEl !== null && rootEl.contains(target)) {
    return;
  }
  emit("close");
}

onMounted(() => {
  window.addEventListener("pointerdown", onDocPointer, true);
});
onUnmounted(() => {
  window.removeEventListener("pointerdown", onDocPointer, true);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-show="open && isJust(image)"
      ref="root"
      class="image-toolbar"
      role="toolbar"
      aria-label="Image tools"
      :style="floatingStyle"
      @pointerdown.stop
    >
      <button
        type="button"
        class="image-toolbar-btn"
        title="Edit description"
        aria-label="Edit image description"
        :disabled="busy"
        @click="emit('describe')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          class="size-3.5"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M4 6h16M4 12h10M4 18h7"
          />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M16 12h4M18 10v4"
          />
        </svg>
      </button>
      <button
        type="button"
        class="image-toolbar-btn"
        title="Crop"
        aria-label="Crop"
        :disabled="busy"
        @click="emit('crop')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          class="size-3.5"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6 3v3M3 6h3M18 21v-3M21 18h-3M21 3h-3M18 6V3M6 18H3M3 21v-3"
          />
        </svg>
      </button>
      <button
        type="button"
        class="image-toolbar-btn"
        title="Rotate 90°"
        aria-label="Rotate 90 degrees"
        :disabled="busy"
        @click="emit('rotate')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          class="size-3.5"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M16 4v4h4M8 20v-4H4M20 8a8 8 0 1 0-2.343 5.657L16 8"
          />
        </svg>
      </button>
      <button
        type="button"
        class="image-toolbar-btn image-toolbar-btn-danger"
        title="Delete image"
        aria-label="Delete image"
        :disabled="busy"
        @click="emit('delete')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          class="size-3.5"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6 7h12M9 7V5h6v2m-7 4v6m4-6v6M8 7l1 12h6l1-12"
          />
        </svg>
      </button>
    </div>
  </Teleport>
</template>

<style scoped>
/* position/left/top are set inline by useAnchoredFloating. */
.image-toolbar {
  z-index: 90;
  display: flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.25rem;
  border: 1px solid color-mix(in oklch, var(--line) 90%, transparent);
  border-radius: 0.45rem;
  background: color-mix(in oklch, var(--bg-raised) 94%, transparent);
  font-family: var(--font-ui);
  pointer-events: auto;
}

.image-toolbar-btn {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 0.3rem;
  display: inline-flex;
  width: 1.75rem;
  height: 1.75rem;
  align-items: center;
  justify-content: center;
}

.image-toolbar-btn:hover:not(:disabled) {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.image-toolbar-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.image-toolbar-btn-danger:hover:not(:disabled) {
  color: var(--danger);
  background: var(--hover-fill);
}

.image-toolbar-btn:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 1px;
}
</style>
